import React, { useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { STRIPE_PUBLIC_KEY } from './stripeConfig';

// loadStripe must be called exactly once per page load — memoise as a module-level singleton
// so re-renders of the provider don't re-fetch the JS bundle.
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise) {
    if (!STRIPE_PUBLIC_KEY) {
      // Defer the warning — surfaced as an inline error in the checkout when needed.
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
  }
  return stripePromise;
};

/**
 * Wrap any subtree that needs Stripe Elements (CardElement, PaymentRequestButton, etc.).
 *
 * Theming hooks into the platform's CSS variables so Card/PaymentRequest UI matches the
 * portal's design system without hardcoded colours.
 */
export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const options = useMemo(
    () => ({
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: 'hsl(263 90% 65%)',
          colorBackground: 'transparent',
          colorText: 'hsl(220 18% 22%)',
          colorDanger: 'hsl(0 84% 60%)',
          fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
          spacingUnit: '4px',
          borderRadius: '10px',
        },
      },
      locale: 'auto' as const,
    }),
    [],
  );

  return <Elements stripe={getStripe()} options={options}>{children}</Elements>;
};
